// Entry state, shared by Home, Journal, the composer and Self.
//
// The server is the source of truth. The previous web client treated IndexedDB as primary and the
// server as a backup blob, which is how the two clients drifted apart; here entries are fetched,
// decrypted in memory, and held for the session. Saving pushes to the server first and then updates
// local state, so what you see is what the iPhone will see.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '../auth/SessionProvider';
import {
  deleteEntry as apiDeleteEntry,
  fetchEntries,
  requestReflection,
  saveEntry as apiSaveEntry,
  type Entry,
  type EntryDraft,
} from '../api/entries';

interface EntriesValue {
  entries: Entry[];
  isLoading: boolean;
  error: string | null;
  refresh(): Promise<void>;
  save(draft: EntryDraft): Promise<Entry>;
  remove(id: string): Promise<void>;
  byId(id: string): Entry | undefined;
}

const EntriesContext = createContext<EntriesValue | null>(null);

export function useEntries(): EntriesValue {
  const ctx = useContext(EntriesContext);
  if (!ctx) throw new Error('useEntries must be used inside <EntriesProvider>');
  return ctx;
}

export function EntriesProvider({ children }: { children: ReactNode }) {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [entries, setEntries] = useState<Entry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!dek) return;
      setError(null);
      try {
        const fetched = await fetchEntries(dek, { limit: 100, signal });
        if (signal?.aborted) return;
        setEntries(sortNewestFirst(fetched));
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load your entries.');
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [dek],
  );

  useEffect(() => {
    const controller = new AbortController();
    setEntries([]);setIsLoading(true);
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const save = useCallback(
    async (draft: EntryDraft): Promise<Entry> => {
      if (!dek) throw new Error('Your vault is locked.');

      const saved = await apiSaveEntry(dek, draft);
      setEntries((current) =>
        sortNewestFirst([saved, ...current.filter((e) => e.id !== saved.id)]),
      );

      // The server reflects in the background after a push, so the insight and portrait are
      // eventually consistent. Kicking it off here means the writing the user just finished starts
      // being read while they're still looking at the app.
      //
      // Not for a draft. Autosave fires while someone is still mid-sentence, and asking Oreia to
      // think about an unfinished thought is both wasteful and presumptuous — the writer hasn't
      // decided they meant it yet.
      if (!saved.isDraft) {
        void requestReflection().catch(() => {
          // Best-effort: a failed nudge just means the nightly pass picks it up instead.
        });
      }

      return saved;
    },
    [dek],
  );

  const remove = useCallback(async (id: string) => {
    // The server confirms the delete before responding, so removing locally afterwards can't be
    // undone by a later pull.
    await apiDeleteEntry(id);
    setEntries((current) => current.filter((e) => e.id !== id));
  }, []);

  const byId = useCallback((id: string) => entries.find((e) => e.id === id), [entries]);

  const value = useMemo<EntriesValue>(
    () => ({ entries, isLoading, error, refresh: () => load(), save, remove, byId }),
    [entries, isLoading, error, load, save, remove, byId],
  );

  return <EntriesContext.Provider value={value}>{children}</EntriesContext.Provider>;
}

function sortNewestFirst(list: Entry[]): Entry[] {
  return [...list].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
